import { useState, useEffect, useMemo, useCallback } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { BsKanban } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import FilterChipsBar from '#globalComponents/Table/FilterChipsBar';
import { useUrlFilters } from '#globalHooks/useUrlFilters';
import { usePreferencesStore } from '#lib/stores/preferencesStore';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import CrmSearchBar from '../../components/common/CrmSearchBar/CrmSearchBar';
import KanbanBoard from '../../components/pipeline/KanbanBoard/KanbanBoard';
import ListView from '../../components/pipeline/ListView/ListView';
import NewDealModal from '../../components/pipeline/NewDealModal/NewDealModal';
import PipelineToolbar from '../../components/pipeline/PipelineToolbar/PipelineToolbar';
import NewPipelineModal from '../../components/settings/NewPipelineModal/NewPipelineModal';
import { useIntegrationsList } from '../../models/integrations/queries';
import { usePipelines } from '../../models/pipelines/queries';
import { useTags } from '../../models/tags/queries';
import {
	buildDealFilterChips,
	buildDealFilterSchema,
	countDealFilters,
	dealFiltersToApiParams,
} from '../../utils/crmTableFilters';

const CrmPipelineBoardPage = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { data: pipelines = [], isLoading: pipelinesLoading } = usePipelines();
	const { data: membersData } = useOrgMembersBasic();
	const members = useMemo(() => membersData?.members ?? [], [membersData?.members]);
	const { data: tagsData } = useTags();
	const tags = useMemo(() => tagsData?.tags ?? [], [tagsData?.tags]);
	const filterSchema = useMemo(() => buildDealFilterSchema({ members, tags }), [members, tags]);
	const { filters, filtersKey, setFilters, removeFilters, clearFilters, page, setPage } = useUrlFilters(filterSchema);
	const lastSelectedCrmPipelineId = usePreferencesStore((s) => s.lastSelectedCrmPipelineId);
	const setLastSelectedCrmPipelineId = usePreferencesStore((s) => s.setLastSelectedCrmPipelineId);
	const [activePipelineId, setActivePipelineId] = useState(null);
	const selectPipeline = useCallback(
		(id) => {
			setActivePipelineId(id);
			setLastSelectedCrmPipelineId(id);
		},
		[setLastSelectedCrmPipelineId],
	);
	const [showNewDeal, setShowNewDeal] = useState(false);
	const [showNewPipeline, setShowNewPipeline] = useState(false);
	const [filterOpen, setFilterOpen] = useState(false);

	// Aircall integration status
	const { data: integrations = [] } = useIntegrationsList();
	const isAircallConnected = integrations.find((i) => i.slug === 'phone_aircall')?.isConnected ?? false;

	const viewMode = searchParams.get('view') || 'kanban';
	const setViewMode = useCallback(
		(mode) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.set('view', mode);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!activePipelineId && pipelines.length > 0) {
			const urlPipelineId = searchParams.get('pipelineId');
			const target =
				(urlPipelineId && pipelines.find((p) => p.id === urlPipelineId)) ??
				(lastSelectedCrmPipelineId && pipelines.find((p) => p.id === lastSelectedCrmPipelineId)) ??
				pipelines.find((p) => p.isDefault) ??
				pipelines[0];
			selectPipeline(target.id);

			if (urlPipelineId) {
				setSearchParams(
					(prev) => {
						const next = new URLSearchParams(prev);
						next.delete('pipelineId');
						return next;
					},
					{ replace: true },
				);
			}
		}
	}, [pipelines, activePipelineId, lastSelectedCrmPipelineId, selectPipeline, searchParams, setSearchParams]);

	const activePipeline = pipelines.find((p) => p.id === activePipelineId);
	const apiFilters = useMemo(() => dealFiltersToApiParams(filters), [filters]);
	const handlePipelineChange = (id) => {
		selectPipeline(id);
		setPage(1);
	};

	if (pipelinesLoading) {
		return (
			<Page width="full">
				<div className="text-bgBase-800">Pipelines werden geladen …</div>
			</Page>
		);
	}

	// No pipelines yet → empty state instead of an unusable empty board.
	if (pipelines.length === 0) {
		return (
			<Page
				width="full"
				scrollMode="content"
				header={<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Pipeline']} />}
			>
				<div className="flex-1 flex items-center justify-center px-6">
					<Card className="max-w-md w-full text-center p-8">
						<div className="mx-auto mb-4 h-12 w-12 rounded-control bg-primary-100 text-primary-600 flex items-center justify-center">
							<BsKanban className="w-6 h-6" />
						</div>
						<h2 className="text-lg font-bold text-text mb-1">Noch keine Pipeline</h2>
						<p className="text-sm text-bgBase-800 mb-6">
							Erstellen Sie Ihre erste Vertriebs-Pipeline – mit dem KI-Assistenten oder manuell.
						</p>
						<Button variant="solid" onClick={() => setShowNewPipeline(true)}>
							Pipeline erstellen
						</Button>
					</Card>
				</div>

				{showNewPipeline && (
					<NewPipelineModal isOpen={showNewPipeline} onClose={() => setShowNewPipeline(false)} />
				)}
			</Page>
		);
	}

	return (
		<Page
			width="full"
			scrollMode="content"
			header={
				<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Pipeline']}>
					<div className="w-64">
						<CrmSearchBar />
					</div>
				</PageHeader>
			}
		>
			<div className="pb-6 flex flex-col flex-1 min-h-0">
				<PipelineToolbar
					pipelines={pipelines}
					activePipelineId={activePipelineId}
					onPipelineChange={handlePipelineChange}
					viewMode={viewMode}
					onViewModeChange={setViewMode}
					filters={filters}
					onFilterChange={setFilters}
					onResetFilters={clearFilters}
					members={members}
					tags={tags}
					activeFilterCount={countDealFilters(filters)}
					filterOpen={filterOpen}
					onFilterToggle={() => setFilterOpen(!filterOpen)}
					onNewDeal={() => setShowNewDeal(true)}
				/>

				<FilterChipsBar
					chips={buildDealFilterChips(filters, filterSchema)}
					onRemove={removeFilters}
					onClearAll={clearFilters}
				/>

				{activePipeline ? (
					viewMode === 'kanban' ? (
						<KanbanBoard
							pipeline={activePipeline}
							filters={apiFilters}
							onOpenDeal={(companyId) => navigate(`/crm/companies/${companyId}`)}
							onNewDeal={() => setShowNewDeal(true)}
						/>
					) : (
						<ListView
							pipelineId={activePipelineId}
							stages={activePipeline.stages ?? []}
							filters={apiFilters}
							filtersKey={filtersKey}
							page={page}
							onPageChange={setPage}
							onOpenDeal={(companyId) => navigate(`/crm/companies/${companyId}`)}
							aircallConnected={isAircallConnected}
						/>
					)
				) : null}
			</div>

			{showNewDeal && activePipeline && (
				<NewDealModal
					isOpen={showNewDeal}
					onClose={() => setShowNewDeal(false)}
					pipelineId={activePipeline.id}
					stages={activePipeline.stages ?? []}
				/>
			)}
		</Page>
	);
};

export default CrmPipelineBoardPage;
